import React, { createContext, useEffect, useReducer, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useProductStatus, ProductType } from 'hooks/use-product-status.hook';
import { FETCH_CLEAN_KWHS_QUERY } from './clean-kwh.api';
import { sortCleanKwhs } from './clean-kwh.utils';

export const CleanKwh = createContext<any>(undefined);

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const cleanKwhs = sortCleanKwhs(action.payload.plan.cleanKilowattHours);
      const [cleanKwh] = cleanKwhs.slice(-1);
      const cleanKwhIndex = cleanKwhs.findIndex(
        item => item?.id === cleanKwh.id
      );
      return { ...state, cleanKwhs, cleanKwh, cleanKwhIndex };
    }
    case 'PREVIOUS': {
      const cleanKwhIndex = state.cleanKwhIndex - 1;
      return {
        ...state,
        cleanKwhIndex,
        cleanKwh: state.cleanKwhs[cleanKwhIndex],
      };
    }
    case 'NEXT': {
      const cleanKwhIndex = state.cleanKwhIndex + 1;
      return {
        ...state,
        cleanKwhIndex,
        cleanKwh: state.cleanKwhs[cleanKwhIndex],
      };
    }
    default:
      throw new Error('unknown event type');
  }
};

interface Props {
  children?: React.ReactNode;
}

export const CleanKwhProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(stateReducer, {});
  const { planId } = useParams<{ planId?: string }>();
  const { data, loading, error } = useQuery<
    FetchCleanKwhsQuery,
    FetchCleanKwhsQueryVariables
  >(FETCH_CLEAN_KWHS_QUERY, {
    variables: { planId: Number(planId) },
  });
  const connectedWind = useProductStatus(
    ProductType.ConnectedWind,
    Number(planId)
  );
  useEffect(() => {
    if (data) dispatch({ payload: data, type: 'INIT' });
  }, [data]);

  const { cleanKwhs, cleanKwh, cleanKwhIndex } = state;
  const previousDisabled = cleanKwhIndex <= 0;
  const nextDisabled = cleanKwhs && cleanKwhIndex >= cleanKwhs.length - 1;
  const previous = () => dispatch({ type: 'PREVIOUS' });
  const next = () => dispatch({ type: 'NEXT' });
  const { isActive, isUnavailable } = connectedWind?.data || {};
  const contextError = error || connectedWind.error;
  const contextLoading =
    loading ||
    (!cleanKwh && !error) ||
    connectedWind.loading ||
    (!connectedWind.data && !connectedWind.error);
  const value = useMemo(
    () => ({
      cleanKwh,
      cleanKwhIndex,
      cleanKwhs,
      connectedWind: {
        isActive,
        isUnavailable,
      },
      error: contextError,
      loading: contextLoading,
      next,
      nextDisabled,
      previous,
      previousDisabled,
    }),
    [
      cleanKwh,
      cleanKwhIndex,
      cleanKwhs,
      contextError,
      contextLoading,
      isActive,
      isUnavailable,
    ]
  );

  return <CleanKwh.Provider value={value}>{children}</CleanKwh.Provider>;
};
